import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Calendar,
  CheckCircle,
  ArrowRight,
  X,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Booking modal state
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    notes: '',
  });
  const [bookingSubmitted, setBookingSubmitted] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleBookingChange = (e) => {
    setBookingData({ ...bookingData, [e.target.name]: e.target.value });
    setBookingError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        language: language,
      });
      
      setIsSubmitted(true);
      console.log('Contact submitted:', response.data);
      
      // Reset after 4 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      }, 4000);
    } catch (err) {
      console.error('Error submitting contact:', err);
      setError(
        language === 'hu' 
          ? 'Hiba történt. Kérjük próbálja újra.' 
          : language === 'de'
          ? 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'
          : 'An error occurred. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingLoading(true);
    setBookingError(null);

    try {
      const response = await axios.post(`${API}/bookings`, {
        ...bookingData,
        language: language,
      });
      
      setBookingSubmitted(true);
      console.log('Booking submitted:', response.data);
      
      // Reset after 4 seconds and close modal
      setTimeout(() => {
        setBookingSubmitted(false);
        setIsBookingOpen(false);
        setBookingData({
          name: '',
          email: '',
          company: '',
          phone: '',
          preferred_date: '',
          preferred_time: '',
          notes: '',
        });
      }, 4000);
    } catch (err) {
      console.error('Error submitting booking:', err);
      setBookingError(
        language === 'hu' 
          ? 'Hiba történt. Kérjük próbálja újra.' 
          : language === 'de'
          ? 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'
          : 'An error occurred. Please try again.'
      );
    } finally {
      setBookingLoading(false);
    }
  };

  const bookingLabels = {
    en: {
      title: 'Book a Consultation',
      description: 'Schedule a free 30-minute consultation with our experts.',
      name: 'Full Name',
      email: 'Email Address',
      company: 'Company Name',
      phone: 'Phone Number',
      date: 'Preferred Date',
      time: 'Preferred Time',
      notes: 'Additional Notes',
      submit: 'Request Consultation',
      success: 'Booking request received! We will confirm shortly.',
    },
    de: {
      title: 'Beratung buchen',
      description: 'Vereinbaren Sie ein kostenloses 30-minütiges Beratungsgespräch.',
      name: 'Vollständiger Name',
      email: 'E-Mail-Adresse',
      company: 'Firmenname',
      phone: 'Telefonnummer',
      date: 'Gewünschtes Datum',
      time: 'Gewünschte Uhrzeit',
      notes: 'Zusätzliche Anmerkungen',
      submit: 'Beratung anfragen',
      success: 'Buchungsanfrage erhalten! Wir bestätigen in Kürze.',
    },
    hu: {
      title: 'Konzultáció foglalás',
      description: 'Foglaljon ingyenes 30 perces konzultációt szakértőinkkel.',
      name: 'Teljes név',
      email: 'E-mail cím',
      company: 'Cégnév',
      phone: 'Telefonszám',
      date: 'Preferált dátum',
      time: 'Preferált időpont',
      notes: 'További megjegyzések',
      submit: 'Konzultáció kérése',
      success: 'Foglalási kérelem megérkezett! Hamarosan visszaigazoljuk.',
    },
  };

  const labels = bookingLabels[language] || bookingLabels.en;

  return (
    <section id="contact" className="relative py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:80px_80px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-4">
            {t.contact.sectionTag}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {t.contact.title}
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 lg:p-10">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6 animate-bounce">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {t.contact.form.success}
                </h3>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-300">
                      {t.contact.form.name} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">
                      {t.contact.form.email} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-slate-300">
                      {t.contact.form.company}
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-300">
                      {t.contact.form.phone}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-300">
                    {t.contact.form.message} *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20 resize-none"
                  />
                </div>

                {error && (
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold py-6 text-lg shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                      Sending...
                    </div>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      {t.contact.form.submit}
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>

          {/* Right Side - Booking & Info */}
          <div className="space-y-8">
            {/* Booking Card */}
            <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 backdrop-blur-sm border border-amber-500/30 rounded-3xl p-8">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center mb-6">
                <Calendar className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                {t.contact.booking.title}
              </h3>
              <p className="text-slate-300 mb-6">
                {t.contact.booking.description}
              </p>
              <Button
                className="w-full bg-white hover:bg-slate-100 text-slate-900 font-semibold py-6 text-lg transition-all duration-300 group"
                onClick={() => setIsBookingOpen(true)}
              >
                {t.contact.booking.cta}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </div>

            {/* Contact Info */}
            <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Contact Information</h3>
              <div className="space-y-6">
                <a
                  href={`mailto:${t.contact.info.email}`}
                  className="flex items-center gap-4 text-slate-300 hover:text-amber-400 transition-colors duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors duration-300">
                    <Mail className="w-5 h-5" />
                  </div>
                  <span>{t.contact.info.email}</span>
                </a>
                <a
                  href={`tel:${t.contact.info.phone}`}
                  className="flex items-center gap-4 text-slate-300 hover:text-amber-400 transition-colors duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors duration-300">
                    <Phone className="w-5 h-5" />
                  </div>
                  <span>{t.contact.info.phone}</span>
                </a>
                <div className="flex items-center gap-4 text-slate-300">
                  <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <span>{t.contact.info.address}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              {labels.title}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {labels.description}
            </DialogDescription>
          </DialogHeader>

          {bookingSubmitted ? (
            <div className="flex flex-col items-center justify-center text-center py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4 animate-bounce">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-lg text-white">{labels.success}</p>
            </div>
          ) : (
            <form onSubmit={handleBookingSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{labels.name} *</Label>
                  <Input
                    name="name"
                    value={bookingData.name}
                    onChange={handleBookingChange}
                    required
                    className="bg-slate-800 border-slate-600 text-white focus:border-amber-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{labels.email} *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={bookingData.email}
                    onChange={handleBookingChange}
                    required
                    className="bg-slate-800 border-slate-600 text-white focus:border-amber-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{labels.company}</Label>
                  <Input
                    name="company"
                    value={bookingData.company}
                    onChange={handleBookingChange}
                    className="bg-slate-800 border-slate-600 text-white focus:border-amber-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{labels.phone}</Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={bookingData.phone}
                    onChange={handleBookingChange}
                    className="bg-slate-800 border-slate-600 text-white focus:border-amber-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{labels.date}</Label>
                  <Input
                    name="preferred_date"
                    type="date"
                    value={bookingData.preferred_date}
                    onChange={handleBookingChange}
                    className="bg-slate-800 border-slate-600 text-white focus:border-amber-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{labels.time}</Label>
                  <Input
                    name="preferred_time"
                    type="time"
                    value={bookingData.preferred_time}
                    onChange={handleBookingChange}
                    className="bg-slate-800 border-slate-600 text-white focus:border-amber-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{labels.notes}</Label>
                <Textarea
                  name="notes"
                  value={bookingData.notes}
                  onChange={handleBookingChange}
                  rows={3}
                  className="bg-slate-800 border-slate-600 text-white focus:border-amber-500 resize-none"
                />
              </div>

              {bookingError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {bookingError}
                </div>
              )}

              <Button
                type="submit"
                disabled={bookingLoading}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold py-5 transition-all duration-300"
              >
                {bookingLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
                  </div>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {labels.submit}
                  </span>
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactSection;
